import React, { PropsWithChildren, forwardRef } from 'react';
import { OptionalLink, OptionalLinkProps } from '../01-core/OptionalLink';
import { Icon } from './Icon';

export interface ButtonProps extends PropsWithChildren, OptionalLinkProps {
  /** The button type */
  type?: 'primary' | 'secondary' | 'tertiary' | 'plain';
  /** How large should the button be? */
  size?: 'small' | 'medium' | 'large';
  /** Button label */
  label?: string;
  /** Button title */
  title?: string;
  /** Button link */
  href?: string;
  /** Button link target */
  target?: HTMLAnchorElement['target'];
  /** Button icon */
  icon?: string;
  /** Button icon in dark mode */
  icon_dark?: string;
  /** Render icon after label */
  icon_after?: boolean;
  /** Children contains an icon */
  icon_children?: boolean;
  /** Disabled */
  disabled?: boolean;
  /** Submit button */
  submit?: boolean;
  /** Reset button */
  reset?: boolean;
  /** Discreet button */
  discreet?: boolean;
  /** Additional class */
  className?: string;
  /** Optional click handler */
  onClick?: (e: React.MouseEvent<HTMLButtonElement> | MouseEvent) => void;
  /** Optional tab index */
  tabIndex?: number;
}

/** A generic button */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = forwardRef(({ ...args }: ButtonProps, ref?: any) => {
  const {
    type = 'secondary',
    size = 'medium',
    label,
    title,
    href,
    target,
    icon,
    icon_dark,
    icon_after,
    icon_children,
    disabled,
    submit,
    reset,
    discreet,
    className,
    onClick,
    children,
    ...props
  } = args;
  const classNames = ['btn', `btn--${type}`, `btn--${size}`, className];
  if (!label && (icon || icon_children)) {
    classNames.push('btn--icon-only');
  }
  if ((icon || icon_children) && icon_after) {
    classNames.push('btn--icon-after');
  }
  if (discreet) {
    classNames.push('btn--discreet');
  }
  if (disabled) {
    classNames.push('btn--disabled');
  }
  return (
    <OptionalLink
      ref={ref}
      button={!!onClick}
      span={!onClick}
      type={
        href || !onClick
          ? undefined
          : submit
            ? 'submit'
            : reset
              ? 'reset'
              : 'button'
      }
      className={classNames.join(' ')}
      href={href}
      target={target}
      title={title}
      disabled={!disabled ? undefined : true}
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      {icon && <Icon className="btn__icon" icon={icon} icon_dark={icon_dark} />}
      {label}
      {icon_children && <span className="btn__icon">{children}</span>}
      {!icon_children && children}
    </OptionalLink>
  );
});
